import { Table, Text, List, VStack, Skeleton } from "@chakra-ui/react"
import { InstallationType } from "@hydrologiq/common/types/monitoring/installation"
import { IANATimeZone } from "@hydrologiq/common/helpers/date"
import { toKey } from "../test/toKey"
import Popover from "@hydrologiq/shared/chakra/Popover"
import { UsageRow, RowType as RowTypes } from "./rows"
import { usageFields, usageHeading } from "./usageCsv"
interface ReportPreviewProps<RowType> {
  rows: RowType[]
  type?: InstallationType
  loading?: boolean
  visibleFields?: (keyof RowTypes)[]
}

const InfoPopover = (props: { identifier: string; header: string; label?: string }) => {
  const { identifier, header, label } = props
  return (
    <Popover
      placement="right-end"
      iconType="info"
      aria-label={`${identifier} information`}
      additionalContent={header}
      ariaLabel={label}
    >
      <VStack minWidth={"300px"} padding={4} margin={0} gap={2}>
        <Text>This number includes CO2 emissions from:</Text>
        <List.Root width={"100%"}>
          <List.Item>Fuel production</List.Item>
          <List.Item>Fuel transportation</List.Item>
          <List.Item>Tailpipe / end-use</List.Item>
        </List.Root>
      </VStack>
    </Popover>
  )
}

const WeightPopover = (props: { identifier: string; header: string; label?: string }) => {
  const { identifier, header, label } = props
  return (
    <Popover
      placement="right-end"
      iconType="info"
      aria-label={`${identifier} information`}
      additionalContent={header}
      ariaLabel={label}
    >
      <VStack minWidth={"300px"} padding={4} margin={0} gap={2}>
        <Text>
          This number is the total weight consumed during the period. This may be different from the weight start/end
          weight due to refuelling and environmental factors.
        </Text>
      </VStack>
    </Popover>
  )
}

function ReportPreview<RowType>({ rows, loading, visibleFields }: ReportPreviewProps<RowType>) {
  const fields = usageFields(rows as RowTypes[], visibleFields)
  const columns = fields.map((field) => usageHeading(field))

  const popovers = {
    totalEmissions: {
      identifier: "CO2e total",
      label: "CO2e total information",
      element: InfoPopover,
    },
    emissionsFactor: {
      identifier: "CO2e factor",
      label: "CO2e factor information",
      element: InfoPopover,
    },
    usedWeight: {
      identifier: "Weight used",
      label: "Weight used information",
      element: WeightPopover,
    },
  } as Record<
    keyof UsageRow,
    {
      identifier: string
      label: string
      element?: React.ComponentType<{ header: string; identifier: string; label: string }>
    }
  >
  const textAlign = {
    start: "left",
    end: "left",
    peakPowerTime: "left",
  } as Record<keyof UsageRow, "left" | "right">

  return (
    <Table.ScrollArea>
      <Table.Root variant="outline" interactive>
        <Table.Caption textAlign={"left"} p={3}>
          Reporting preview (date timezone is {IANATimeZone()})
        </Table.Caption>
        <Table.Header>
          <Table.Row>
            {columns.map((column, index) => {
              const field = fields[index]
              const popover = popovers[field as keyof UsageRow]
              return (
                <Table.ColumnHeader textAlign={textAlign[field as keyof UsageRow] || "right"} key={column}>
                  {popover && popover.element ? (
                    <popover.element header={column} identifier={popover.identifier} label={popover.label} />
                  ) : (
                    column
                  )}
                </Table.ColumnHeader>
              )
            })}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {loading
            ? [...Array(10)].map((_, index) => (
                <Table.Row key={index}>
                  <Table.Cell colSpan={10}>
                    <Skeleton height="20px" width="100%" />
                  </Table.Cell>
                </Table.Row>
              ))
            : rows.map((row: RowType) => {
                const base = row as UsageRow
                return (
                  <Table.Row key={toKey(`${base.start}-${base.end}`)}>
                    {fields.map((field) => (
                      <Table.Cell key={field} textAlign={textAlign[field as keyof UsageRow] || "right"}>
                        {base[field as keyof UsageRow]}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                )
              })}
        </Table.Body>
      </Table.Root>
    </Table.ScrollArea>
  )
}

export default ReportPreview
